import React from 'react';
import { 
  Box, 
  Text, 
  Heading, 
  VStack, 
  HStack, 
  Progress, 
  Stat, 
  StatLabel, 
  StatNumber, 
  StatHelpText,
  Divider,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Tooltip
} from '@chakra-ui/react';
import { FaInfoCircle, FaCoins, FaTrophy, FaTicketAlt } from 'react-icons/fa';
import { useGame } from '../../context/GameContext';

const PoolInfo = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { gameState } = useGame();
  const { pools } = gameState;

  return (
    <>
      <Box 
        p={4} 
        bg="whiteAlpha.100" 
        borderRadius="md" 
        boxShadow="sm"
        _hover={{ bg: "whiteAlpha.200" }}
        transition="all 0.3s"
        height="100%"
        display="flex"
        flexDirection="column"
      >
        <VStack spacing={3} align="stretch" width="100%">
          <HStack justify="space-between">
            <Heading size="sm" color="purple.300">Prize Pools</Heading>
            <Button 
              size="sm" 
              variant="outline" 
              colorScheme="purple" 
              leftIcon={<FaInfoCircle />}
              onClick={onOpen}
            >
              Details
            </Button>
          </HStack>
          
          <Box mt={2}>
            <Tooltip label="75% of all bets go to the main prize pool">
              <Text fontSize="sm" color="gray.400" mb={1}>
                <FaCoins style={{ display: 'inline', marginRight: '5px' }} /> Main Prize Pool:
              </Text>
            </Tooltip>
            <HStack>
              <Box position="relative" width="100%">
                <Box 
                  position="absolute"
                  top="0"
                  left="0"
                  right="0"
                  bottom="0"
                  borderRadius="md"
                  bg="gray.600"
                />
                <Box 
                  position="absolute"
                  top="0"
                  left="0"
                  height="100%"
                  width={`${(pools.prizePool / pools.totalBets) * 100}%`}
                  borderRadius="md"
                  bg="green.400"
                />
                <Box height="24px" width="100%" />
              </Box>
              <Text fontWeight="bold" color="white" width="100px" textAlign="right">
                {pools.prizePool.toFixed(2)}
              </Text>
            </HStack>
          </Box>
          
          <Box>
            <Tooltip label="10% of all bets go to the VIP pool, allocated to genesis holders according to tokenomics">
              <Text fontSize="sm" color="gray.400" mb={1}>
                <FaTrophy style={{ display: 'inline', marginRight: '5px' }} /> VIP Pool:
              </Text>
            </Tooltip>
            <HStack>
              <Box position="relative" width="100%">
                <Box 
                  position="absolute"
                  top="0"
                  left="0"
                  right="0"
                  bottom="0"
                  borderRadius="md"
                  bg="gray.600"
                />
                <Box 
                  position="absolute"
                  top="0"
                  left="0"
                  height="100%"
                  width={`${(pools.vipPool / pools.totalBets) * 100}%`}
                  borderRadius="md"
                  bg="purple.400"
                />
                <Box height="24px" width="100%" />
              </Box>
              <Text fontWeight="bold" color="white" width="100px" textAlign="right">
                {pools.vipPool.toFixed(2)}
              </Text>
            </HStack>
          </Box>
          
          <Box>
            <Tooltip label="2% of all bets go to the lottery pool">
              <Text fontSize="sm" color="gray.400" mb={1}>
                <FaTicketAlt style={{ display: 'inline', marginRight: '5px' }} /> Lottery Pool:
              </Text>
            </Tooltip>
            <HStack>
              <Box position="relative" width="100%">
                <Box 
                  position="absolute"
                  top="0"
                  left="0"
                  right="0"
                  bottom="0"
                  borderRadius="md"
                  bg="gray.600"
                />
                <Box 
                  position="absolute"
                  top="0"
                  left="0"
                  height="100%"
                  width={`${(pools.lotteryPool / pools.totalBets) * 100}%`}
                  borderRadius="md"
                  bg="yellow.400"
                />
                <Box height="24px" width="100%" />
              </Box>
              <Text fontWeight="bold" color="white" width="100px" textAlign="right">
                {pools.lotteryPool.toFixed(2)}
              </Text>
            </HStack>
          </Box>
        </VStack>
      </Box>

      {/* Pool Details Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay backdropFilter="blur(10px)" />
        <ModalContent bg="purple.900" color="white" borderRadius="xl" boxShadow="0 8px 32px 0 rgba(31, 38, 135, 0.37)">
          <ModalHeader borderBottomWidth="1px" borderColor="purple.700">
            <HStack>
              <FaCoins />
              <Text>Prize Pool Details</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            <VStack spacing={5} align="stretch">
              <Box>
                <Heading size="sm" mb={2} color="purple.300">How Prize Pools Work</Heading>
                <Text fontSize="sm">
                  For every bet placed, a portion is allocated to different prize pools:
                </Text>
                <VStack mt={2} align="start" pl={4}>
                  <Text fontSize="sm">• <strong>75%</strong> goes to the Main Prize Pool for regular payouts</Text>
                  <Text fontSize="sm">• <strong>10%</strong> goes to the VIP Pool for special rewards, allocated to genesis holders according to tokenomics</Text>
                  <Text fontSize="sm">• <strong>2%</strong> goes to the Lottery Pool for weekly drawings</Text>
                </VStack>
              </Box>
              
              <Divider borderColor="purple.700" />
              
              <Box>
                <Heading size="sm" mb={3} color="purple.300">Current Pool Totals</Heading>
                <HStack spacing={4} justify="space-between">
                  <Stat>
                    <StatLabel>Main Prize</StatLabel>
                    <StatNumber>{pools.prizePool.toFixed(2)}</StatNumber>
                    <StatHelpText>75% of all bets</StatHelpText>
                  </Stat>
                  <Stat>
                    <StatLabel>VIP Pool</StatLabel>
                    <StatNumber>{pools.vipPool.toFixed(2)}</StatNumber>
                    <StatHelpText>10% of all bets, allocated to genesis holders according to tokenomics</StatHelpText>
                  </Stat>
                  <Stat>
                    <StatLabel>Lottery Pool</StatLabel>
                    <StatNumber>{pools.lotteryPool.toFixed(2)}</StatNumber>
                    <StatHelpText>2% of all bets</StatHelpText>
                  </Stat>
                </HStack>
              </Box>
              
              <Divider borderColor="purple.700" />
              
              <Box>
                <Heading size="sm" mb={3} color="purple.300">Total Bets Placed</Heading>
                <Text fontSize="lg" fontWeight="bold">
                  {pools.totalBets.toFixed(2)}
                </Text>
              </Box>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default PoolInfo;
